// CB:BEGIN admin-stats-cards-real
/**
 * AdminStatsCards — KPI metric strip for the admin command center.
 *
 * 4 cards in a responsive row: Users, Projects, Bids, Revenue.
 * Each card shows a headline metric + sub-breakdown rows.
 * Clickable rows navigate to the relevant admin list.
 */
import React from 'react';
import { useApi } from '@/lib/useApi';
import { useQuery } from '@tanstack/react-query';

interface DashboardStats {
  users: {
    total: number;
    active: number;
    byRole: { ADMIN: number; CONTRACTOR: number; HOMEOWNER: number; REALTOR: number; PROPERTY_MANAGER: number };
  };
  projects: {
    total: number;
    byStatus: { BIDDING: number; IN_PROGRESS: number; COMPLETED: number };
  };
  bids: { total: number; accepted: number; acceptanceRate: number };
  revenue: { cents: number; dollars: number };
  gmv?: { cents: number; dollars: number };
  generatedAt: string;
}

// ─── Internal atoms ───────────────────────────────────────────────────────────

const ChevronRight = () => (
  <svg width="10" height="10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} style={{ flexShrink: 0, opacity: 0.45 }}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
  </svg>
);

interface SubRowProps {
  label: string;
  value: string | number;
  onClick?: () => void;
}
const SubRow: React.FC<SubRowProps> = ({ label, value, onClick }) => {
  const inner = (
    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <span style={{ fontWeight: 600, color: '#374151', fontSize: 13 }}>{value}</span>
      <span style={{ color: '#9ca3af', fontSize: 12 }}>{label}</span>
      {onClick && <ChevronRight />}
    </span>
  );
  if (!onClick) return <span>{inner}</span>;
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', lineHeight: 'inherit' }}
    >
      {inner}
    </button>
  );
};

const Dot = () => <span style={{ color: '#e5e7eb', fontSize: 12, margin: '0 4px', userSelect: 'none' }}>·</span>;

interface KpiCardProps {
  icon: React.ReactNode;
  gradient: string;
  label: string;
  metric: string | number;
  metricSub?: string;
  children?: React.ReactNode;
  testId?: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ icon, gradient, label, metric, metricSub, children, testId }) => (
  <div
    data-testid={testId}
    style={{
      flex: 1,
      minWidth: 180,
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: 10,
      padding: '16px 18px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    }}
  >
    {/* Header */}
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
      <div style={{
        width: 38, height: 38, flexShrink: 0,
        background: gradient, borderRadius: 9,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {icon}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 2 }}>
          {label}
        </div>
        <div style={{ fontSize: 26, fontWeight: 700, color: '#111827', lineHeight: 1, display: 'flex', alignItems: 'baseline', gap: 4 }}>
          {metric}
          {metricSub && (
            <span style={{ fontSize: 12, fontWeight: 400, color: '#9ca3af' }}>{metricSub}</span>
          )}
        </div>
      </div>
    </div>
    {/* Sub rows */}
    {children && (
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '2px 0', borderTop: '1px solid #f3f4f6', paddingTop: 10 }}>
        {children}
      </div>
    )}
  </div>
);

// ─── Icons ────────────────────────────────────────────────────────────────────

const IconUsers = () => (
  <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
    <circle cx="9" cy="7" r="4" stroke="white" strokeWidth={1.8} fill="none" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
  </svg>
);

const IconFolder = () => (
  <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
  </svg>
);

const IconClipboard = () => (
  <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
  </svg>
);

const IconCurrency = () => (
  <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
  </svg>
);

// ─── Main component ───────────────────────────────────────────────────────────

export default function AdminStatsCards({ onNavigate }: { onNavigate?: (screen: string) => void }) {
  const api = useApi();

  const { data: stats, isLoading } = useQuery<DashboardStats>({
    queryKey: ['adminStats'],
    queryFn: async () => {
      try {
        const res = await api.get('/api/admin/stats');
        return {
          users:   res?.users   || { total: 0, active: 0, byRole: { ADMIN: 0, CONTRACTOR: 0, REALTOR: 0, HOMEOWNER: 0, PROPERTY_MANAGER: 0 } },
          projects:res?.projects|| { total: 0, byStatus: { BIDDING: 0, IN_PROGRESS: 0, COMPLETED: 0 } },
          bids:    res?.bids    || { total: 0, accepted: 0, acceptanceRate: 0 },
          revenue: res?.revenue || { cents: 0, dollars: 0 },
          gmv:     res?.gmv    || { cents: 0, dollars: 0 },
          generatedAt: res?.generatedAt || new Date().toISOString(),
        };
      } catch {
        return { users: { total: 0, active: 0, byRole: { ADMIN: 0, CONTRACTOR: 0, REALTOR: 0, HOMEOWNER: 0, PROPERTY_MANAGER: 0 } }, projects: { total: 0, byStatus: { BIDDING: 0, IN_PROGRESS: 0, COMPLETED: 0 } }, bids: { total: 0, accepted: 0, acceptanceRate: 0 }, revenue: { cents: 0, dollars: 0 }, gmv: { cents: 0, dollars: 0 }, generatedAt: new Date().toISOString() };
      }
    },
    staleTime: 1000 * 60 * 5,
  });

  if (isLoading) {
    return (
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {[0, 1, 2, 3].map(i => (
          <div key={i} style={{ flex: 1, minWidth: 180, height: 110, background: '#f3f4f6', borderRadius: 10, animation: 'pulse 1.5s infinite' }} />
        ))}
      </div>
    );
  }
  if (!stats) return null;

  const u = stats.users?.byRole ?? { HOMEOWNER: 0, CONTRACTOR: 0, REALTOR: 0, ADMIN: 0, PROPERTY_MANAGER: 0 };
  const p = stats.projects?.byStatus ?? { BIDDING: 0, IN_PROGRESS: 0, COMPLETED: 0 };
  const acceptRate = stats.bids?.total > 0 ? Math.round((stats.bids.accepted / stats.bids.total) * 100) : 0;

  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }} data-testid="admin-stats-strip">

      <KpiCard
        testId="stats-users-card"
        gradient="linear-gradient(135deg, #3b82f6, #1d4ed8)"
        icon={<IconUsers />}
        label="Platform Users"
        metric={(stats.users?.total ?? 0).toLocaleString()}
      >
        <SubRow value={u.HOMEOWNER} label="homeowners" onClick={() => onNavigate?.('admin-homeowners-list')} />
        <Dot />
        <SubRow value={u.CONTRACTOR} label="contractors" onClick={() => onNavigate?.('admin-contractors-list')} />
        <Dot />
        <SubRow value={u.REALTOR} label="realtors" />
        <Dot />
        <SubRow value={u.PROPERTY_MANAGER ?? 0} label="property managers" onClick={() => onNavigate?.('admin-property-managers')} />
      </KpiCard>

      <KpiCard
        testId="stats-projects-card"
        gradient="linear-gradient(135deg, #10b981, #059669)"
        icon={<IconFolder />}
        label="Projects"
        metric={(stats.projects?.total ?? 0).toLocaleString()}
      >
        <SubRow value={p.BIDDING} label="bidding" onClick={() => onNavigate?.('admin-projects-list')} />
        <Dot />
        <SubRow value={p.IN_PROGRESS} label="active" onClick={() => onNavigate?.('admin-projects-list')} />
        <Dot />
        <SubRow value={p.COMPLETED} label="done" onClick={() => onNavigate?.('admin-projects-list')} />
      </KpiCard>

      <KpiCard
        testId="stats-bids-card"
        gradient="linear-gradient(135deg, #8b5cf6, #6d28d9)"
        icon={<IconClipboard />}
        label="Bids Accepted"
        metric={(stats.bids?.accepted ?? 0).toLocaleString()}
        metricSub={`/ ${(stats.bids?.total ?? 0).toLocaleString()}`}
      >
        <SubRow value={`${acceptRate}%`} label="acceptance rate" />
      </KpiCard>

      <KpiCard
        testId="stats-revenue-card"
        gradient="linear-gradient(135deg, #059669, #047857)"
        icon={<IconCurrency />}
        label="Revenue (fees)"
        metric={`$${(stats.revenue?.dollars ?? 0).toLocaleString()}`}
      >
        <span style={{ fontSize: 12, color: '#9ca3af' }}>
          GMV{' '}
          <span style={{ fontWeight: 600, color: '#374151' }}>
            ${(stats.gmv?.dollars ?? 0).toLocaleString()}
          </span>{' '}
          total homeowner spend
        </span>
      </KpiCard>

    </div>
  );
}
// CB:END admin-stats-cards-real
